import { Component } from "react";
import { logger } from "../utils/logger";

// Guards the one-time auto-reload below against looping forever if the app
// is broken in a way a reload can't fix (rather than the stale-chunk case it
// targets, where a reload fetches the current index.html/chunk manifest and
// self-heals). Session-scoped by design — a *different* crash later in the
// same tab still gets its own reload attempt once this window has passed.
const RELOAD_KEY = "mf_error_boundary_reload_at";
const RELOAD_COOLDOWN_MS = 10000;

// Matches both the "Loading chunk N failed" and "Loading CSS chunk N failed"
// shapes CRA's webpack runtime throws (as ChunkLoadError, or as a plain
// Error for the CSS-chunk variant — hence matching on message text rather
// than error.name alone).
function isChunkLoadError(error) {
  return error?.name === "ChunkLoadError" || /Loading (chunk|CSS chunk) .+ failed/i.test(error?.message || "");
}

function canAutoReload() {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    return Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS;
  } catch {
    // sessionStorage unavailable (private mode, disabled) — allow the reload;
    // worst case is one extra attempt, not a stuck blank page.
    return true;
  }
}

function markReloadAttempted() {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Non-fatal — see canAutoReload.
  }
}

/**
 * Last line of defense around the app's lazy-loaded routes (see App.js's
 * Suspense). Without this, any render/commit-phase error thrown while a
 * visitor is on an LP — most commonly a stale JS/CSS chunk reference after a
 * deploy (ChunkLoadError), or a third-party script (Evina, an in-app
 * webview's own injected SDK, a browser extension) mutating the DOM out from
 * under React and causing its commit phase to throw a removeChild
 * NotFoundError — unmounts the entire React tree with nothing rendered in
 * its place: a blank page, for a visitor who just clicked a paid ad, with no
 * way forward except manually refreshing.
 *
 * Both classes of error self-heal on a fresh load (a reload picks up the
 * current index.html/chunk manifest; a fresh mount starts the DOM over
 * without whatever third-party mutation raced it), so the recovery here is
 * a single automatic reload rather than a dead-end fallback screen — capped
 * by RELOAD_COOLDOWN_MS so a genuinely broken page can't reload-loop.
 */
class RouteErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const chunkError = isChunkLoadError(error);
    const willReload = canAutoReload();

    logger.error("app_crash_recovery", "componentDidCatch", "Something broke unexpectedly", {
      chunkError,
      willReload,
      error: {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
      },
      componentStack: info?.componentStack,
    });

    if (willReload) {
      markReloadAttempted();
      window.location.reload();
    }
  }

  render() {
    // While a reload is in flight (or was just skipped by the cooldown),
    // render nothing rather than whatever partial tree crashed — same
    // fallback={null} behavior Suspense already uses elsewhere in this app,
    // so a caught error doesn't look any different from a normal load.
    if (this.state.hasError) return null;

    return this.props.children;
  }
}

export default RouteErrorBoundary;
